/* ---------------------------------------------------------------------------
 * B4B per-colour sold-out badge (collection grid)
 *
 * The B4B "Variable Products" app splits a multi-colour product into one tile
 * per colour, client-side, by CLONING the theme's <product-item> card and
 * tagging each clone with `data-variant-id` (a variant id of that colour) and
 * the class `variable-products`. B4B's own sold-out badge keys off Shopify
 * native `variant.available` only and has no access to the store's online
 * quantity rule, so it can't badge a colour that is "available" in Shopify but
 * below our selling threshold.
 *
 * snippets/product-item.liquid emits an inert JSON island
 *   <script type="application/json" class="b4b-color-soldout">{label, v:{id:bool}}</script>
 * where `v[variantId] === true` means that variant's COLOUR is sold out under
 * the same rule as the PDP add-to-cart button. B4B clones the card, so the
 * island travels into every colour clone.
 *
 * Performance note: B4B clones dozens of cards in a burst, and the theme
 * toggles classes constantly (scroll-reveal, hover, lazy-load). Reacting to
 * every mutation individually is O(mutations x DOM) and freezes the page, so
 * the observer only SCHEDULES a single batched pass per animation frame. Each
 * card is handled once per `data-variant-id` value (re-handled only if B4B
 * changes the colour), and our own badge writes are made idempotent so they
 * can't cause a feedback loop. Safe no-op when B4B is inactive.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SELECTOR = 'product-item.variable-products, .product-item.variable-products';
  var scheduled = false;

  function getIsland(card) {
    if (card.__lvB4b !== undefined) return card.__lvB4b;
    var data = null;
    var el = card.querySelector('script.b4b-color-soldout');
    if (el) {
      try { data = JSON.parse(el.textContent); } catch (e) { data = null; }
    }
    card.__lvB4b = data;
    return data;
  }

  function addBadge(card, label) {
    card.classList.add('product-item--sold-out');
    if (!card.__lvBadgeEl && !card.querySelector('.label--subdued')) {
      var wrapper = card.querySelector('.product-item__image-wrapper') || card;
      var list = wrapper.querySelector('.product-item__label-list');
      if (!list) {
        list = document.createElement('div');
        list.className = 'product-item__label-list label-list';
        wrapper.insertBefore(list, wrapper.firstChild);
      }
      var span = document.createElement('span');
      span.className = 'label label--subdued';
      span.textContent = label || 'Sold out';
      list.appendChild(span);
      card.__lvBadgeEl = span;
    }
    // A sold-out colour shouldn't advertise a discount/"save" badge. The badge
    // (.label--highlight) was cloned from the available parent card, so hide it.
    var promos = card.querySelectorAll('.label--highlight');
    for (var p = 0; p < promos.length; p++) {
      if (!promos[p].__lvHidden) {
        promos[p].__lvHidden = true;
        promos[p].style.display = 'none';
      }
    }
  }

  function removeBadge(card) {
    if (card.__lvBadgeEl) {
      card.__lvBadgeEl.remove();
      card.__lvBadgeEl = null;
      card.classList.remove('product-item--sold-out');
    }
    // Restore any discount badge we hid (e.g. colour switched back to in-stock).
    var promos = card.querySelectorAll('.label--highlight');
    for (var p = 0; p < promos.length; p++) {
      if (promos[p].__lvHidden) {
        promos[p].__lvHidden = false;
        promos[p].style.display = '';
      }
    }
  }

  function processOne(card) {
    var vid = card.getAttribute('data-variant-id');
    if (!vid) return;                 // B4B hasn't configured this clone yet
    if (card.__lvVid === vid) return; // already handled this colour
    var data = getIsland(card);
    card.__lvVid = vid;
    if (!data || !data.v) return;     // not a colour product / no data
    if (data.v[vid] === true) addBadge(card, data.label);
    else removeBadge(card);
  }

  function runPass() {
    scheduled = false;
    var cards = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < cards.length; i++) processOne(cards[i]);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout (not requestAnimationFrame): rAF is paused while the tab is
    // hidden/backgrounded, which would leave `scheduled` stuck and the badges
    // never applied until the tab gains focus. The flag still coalesces a
    // mutation burst into one pass per tick, so there's no per-mutation storm.
    setTimeout(runPass, 50);
  }

  function init() {
    schedule();

    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-variant-id']
      });
    }

    // Bounded safety sweeps for late B4B work (stops after ~10s).
    var ticks = 0;
    var iv = setInterval(function () {
      schedule();
      if (++ticks >= 20) clearInterval(iv);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', schedule);
})();
